import React, { useMemo } from 'react';

import { ResultSummaryConfig, resolveSummaryConfig } from './quizTypes';
import { SummaryRoot } from './ResultSummary.styles';
import { usePipedText } from './usePipedText';

interface ResultSummaryProps {
  data: { blockConfig?: Partial<ResultSummaryConfig> };
}

const clampPercent = (value: number) => Math.min(100, Math.max(0, Number(value) || 0));

const ResultSummary: React.FC<ResultSummaryProps> = ({ data }) => {
  const config = useMemo(() => resolveSummaryConfig(data?.blockConfig), [data?.blockConfig]);
  const { html: piped, text: pipedText } = usePipedText();
  const position = clampPercent(config.meterPosition);
  const imageUrl = config.imageUrl.trim();

  return (
    <SummaryRoot className="quiz-block">
      {config.title && (
        <h2 className="quiz-title" dangerouslySetInnerHTML={{ __html: piped(config.title) }} />
      )}

      <div className="quiz-card summary-card">
        {imageUrl && (
          <div className="summary-hero">
            <img src={pipedText(imageUrl)} alt="" />
          </div>
        )}

        <div className="summary-body">
          <div className="summary-meter-head">
            <span>{pipedText(config.meterTitle)}</span>
            {config.meterBadge && <span className="summary-badge">{pipedText(config.meterBadge)}</span>}
          </div>

          <div
            className="summary-meter"
            role="meter"
            aria-valuenow={position}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={config.meterTitle}
          >
            <div className="summary-meter-track" />
            <span className="summary-marker" style={{ left: `${position}%` }}>
              {config.meterBadge && (
                <span className="summary-marker-label">{pipedText(config.meterBadge)}</span>
              )}
            </span>
          </div>
          <div className="summary-meter-scale">
            <span>{config.lowLabel}</span>
            <span>{config.midLabel}</span>
            <span>{config.highLabel}</span>
          </div>

          {(config.insightTitle || config.insightText) && (
            <div className="summary-insight">
              {config.insightTitle && (
                <p
                  className="summary-insight-title"
                  dangerouslySetInnerHTML={{ __html: piped(config.insightTitle) }}
                />
              )}
              {config.insightText && (
                <p
                  className="summary-insight-text"
                  dangerouslySetInnerHTML={{ __html: piped(config.insightText) }}
                />
              )}
            </div>
          )}

          {config.details.length > 0 && (
            <ul className="summary-details">
              {config.details.map((detail) => (
                <li key={detail.id} className="summary-detail">
                  {detail.icon && (
                    <span className="summary-detail-icon" aria-hidden="true">
                      {detail.icon}
                    </span>
                  )}
                  <span>
                    <span className="summary-detail-label">{detail.label}</span>
                    <span
                      className="summary-detail-text"
                      dangerouslySetInnerHTML={{ __html: piped(detail.text) }}
                    />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SummaryRoot>
  );
};

export default ResultSummary;
